import styled from 'styled-components';

import { ds } from '@/config/colors';
import { media } from '@/utils';

/* Wysokość paska górnego jak w hotelspot-audit (h-14, od desktopu h-20). */
export const TOP_BAR_HEIGHT = 64;
export const TOP_BAR_HEIGHT_COMPACT = 56;
/* Wąski pasek menu z ikonami (AppSidebar w audycie: 72 px). */
export const SIDEBAR_RAIL_WIDTH = 72;
/*
 * Kontener aplikacji jak w hotelspot-audit (container-app): pasek górny, menu i treść leżą w jednej
 * kolumnie o maks. szerokości 1760 px z marginesem 32 px, wyśrodkowanej na ekranie. Po bokach zostaje
 * pusty margines (--hs-gutter). Treść stron ma 100% szerokości tej kolumny, a pasek przewijania zostaje
 * przy krawędzi okna (przewija się obszar treści, który sięga do prawej krawędzi).
 */
export const APP_MAX_WIDTH = 1760;
export const APP_SIDE_PADDING = 32;
/* Odstęp treści od paska menu (gap-12 w audycie). */
export const CONTENT_GAP = 48;
/** Szerokość rozwiniętego (i przypiętego) sidebara, jak panel po najechaniu. */
export const SIDEBAR_PINNED_WIDTH = 272;
export const APP_GUTTER = 'var(--hs-gutter)';

interface LayoutWrapperProps {
    $isMenuOpen?: boolean;
    $chatOpen?: boolean;
    $sidebarPinned?: boolean;
}

export const LayoutWrapper = styled.section<LayoutWrapperProps>`
    min-height: 100vh;
    height: 100%;
    display: grid;
    overflow: auto;
    grid-template-columns: auto auto 1fr;
    grid-template-rows: auto auto 2fr;
    /* Rama aplikacji (sidebar + topbar) jest biała; dzięki temu zaokrąglony
       lewy górny róg obszaru treści odsłania biały „notch". */
    background: ${({ theme }) => (theme.mode === 'dark' ? '#1a1f26' : '#ffffff')};

    ${media.desktop} {
        /* Układ jak w audycie: pasek górny na całą szerokość, pod nim wąskie menu
           z ikonami i treść, która przewija się w środku. */
        --hs-gutter: max(${APP_SIDE_PADDING}px, calc((100vw - ${APP_MAX_WIDTH}px) / 2 + ${APP_SIDE_PADDING}px));
        height: 100vh;
        height: 100dvh;
        overflow: hidden;
        /* Przypięty sidebar zajmuje pełną szerokość panelu, treść przesuwa się w prawo. */
        grid-template-columns: var(--hs-gutter) ${({ $sidebarPinned }) => ($sidebarPinned ? SIDEBAR_PINNED_WIDTH : SIDEBAR_RAIL_WIDTH)}px minmax(0, 1fr);
        transition: grid-template-columns 200ms cubic-bezier(0.2, 0.8, 0.2, 1);
        grid-template-rows: ${TOP_BAR_HEIGHT}px minmax(0, 1fr);
    }

    ${media.tabletDown} {
        grid-template-columns: 1fr;
    }

    ${media.mobile} {
        height: auto;
        min-height: 100vh;
        overflow: visible;
        overflow-x: clip;
    }

    /* Przyciemnienie pod otwartą szufladą menu daje MobileOverlay w Sidebarze. */
`;

export const MainHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;
    height: ${TOP_BAR_HEIGHT}px;
    padding: 0 20px;

    /* Logo na równi z ikonami menu (16 px wcięcia w pasku 72 px), prawa krawędź na granicy kontenera. */
    ${media.desktop} {
        padding: 0 var(--hs-gutter) 0 calc(var(--hs-gutter) + 16px);
    }
    background: ${({ theme }) => (theme.mode === 'dark' ? '#1a1f26' : ds.bgSurface)};

    ${media.tabletDown} {
        height: ${TOP_BAR_HEIGHT_COMPACT}px;
        padding: 0 20px;
    }

    /* Telefon: węższy margines, żeby zmieściły się wszystkie ikony paska. */
    ${media.mobile} {
        padding: 0 12px;
    }
`;

export const Main = styled.div<{ $chatOpen?: boolean }>`
    display: flex;
    flex-direction: column;
    flex: 1;
    /* Treść przewija się także przy otwartym czacie AI (szuflada nie ma tła). */
    overflow: auto;
    /* Miejsce na pasek przewijania zarezerwowane na stałe: treść nie przeskakuje, gdy pasek
       pojawia się i znika podczas doładowywania sekcji (np. statystyk na dashboardzie). */
    scrollbar-gutter: stable;
    /* Treść na bieli (DS bg/surface), bez zaokrąglonego rogu: oddziela ją linia menu, jak w audycie. */
    background: ${({ theme }) => (theme.mode === 'dark' ? '#0f1419' : ds.bgSurface)};
    grid-column-start: 2;
    grid-column-end: -1;
    grid-row-start: 2;
    grid-row-end: -1;
    position: relative;

    ${media.tabletDown} {
        grid-column-start: 1;
        border-top-left-radius: 0;
    }

    ${media.desktop} {
        grid-column: 3 / -1;
        grid-row: 2;
        /* Treść w kolumnie kontenera: odstęp od menu z lewej, margines kontenera z prawej. */
        padding: 0 var(--hs-gutter) 0 ${CONTENT_GAP}px;
    }

    ${media.mobile} {
        overflow: visible;
        /* Bez przewijania w bok: to, co mimo wszystko wystaje, jest przycinane
           (clip nie tworzy kontenera przewijania, więc sticky dalej działa). */
        overflow-x: clip;
        min-width: 0;
        max-width: 100vw;
        padding-top: ${TOP_BAR_HEIGHT_COMPACT}px;
        /* Dolny odstęp daje MobileFooter. */
        padding-bottom: 0;
    }
`;

export const PageHeadline = styled.div`
    grid-column: 2 / -1;
    position: relative;
    z-index: 46; /* nad sidebarem (20, rozwinięty 45), poniżej modali HeroUI (z-50) */
    background: ${({ theme }) => (theme.mode === 'dark' ? '#1a1f26' : ds.bgSurface)};
    /* Linia pod paskiem bez dokładania wysokości (wiersz siatki ma równe 80 px). */
    box-shadow: 0 1px 0 ${({ theme }) => (theme.mode === 'dark' ? '#374151' : ds.borderDefault)};

    ${media.desktop} {
        grid-column: 1 / -1;
        grid-row: 1;
    }

    ${media.tabletDown} {
        grid-column: 1 / -1;
    }

    /* Telefon: pasek przyklejony u góry, ale pod modalami i szufladami HeroUI (z-50),
       żeby dało się je zamknąć. Menu boczne ma własne z-1010. */
    ${media.mobile} {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 49;
        background: ${({ theme }) => theme.colors.topbar.background};
    }
`;

/* Stopka z wersją aplikacji, tylko na telefonie: na końcu treści strony, nad dolnym marginesem. */
export const MobileFooter = styled.footer`
    display: none;

    ${media.mobile} {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-top: auto;
        padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
        border-top: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : ds.borderDefault)};
        font-size: 12px;
        line-height: 1.4;
        color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : ds.textMuted)};
    }
`;
